import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../components/Common/AppOverlay";
import { getLoggedInUser } from "../../helpers/backend_helper";
import InspectionCycleOverview from "./InspectionCycleOverview";
import FullScreenPageLayout from "../../components/Common/FullScreenPageLayout";
import "./InspectionCycleList.scss";

export default function InspectionCyclePreviewPage() {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const origin = location.state?.fromPath;
  const fromEngagement = /^\/engagements\/(list|\d+\/preview)$/.test(origin || "");
  const returnPath = fromEngagement ? origin : "/inspections/list";
  const returnLabel = fromEngagement
    ? origin === "/engagements/list" ? "Engagements" : "Engagement"
    : "Inspection Cycles";
  const [canManage, setCanManage] = useState(false);
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);

  useEffect(() => {
    let current = true;
    getLoggedInUser()
      .then((account) => {
        if (!current) return;
        const roleName =
          account?.role?.name ||
          account?.roleName ||
          account?.userRole?.name ||
          account?.designation ||
          "";
        setCanManage(
          Number(account?.roleId) !== 4 &&
            !/engagement\s+member/i.test(roleName)
        );
      })
      .catch(() => {
        if (current) setCanManage(false);
      });
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    if (!moreMenuOpen) return undefined;

    const closeMenu = (event) => {
      if (!moreMenuRef.current?.contains(event.target)) setMoreMenuOpen(false);
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMoreMenuOpen(false);
    };
    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [moreMenuOpen]);

  const renderActions = () => (
    <div className="inspection-preview-page__actions">
      {canManage && (
        <button
          type="button"
          className="inspection-preview-page__edit"
          onClick={() => navigate(`/inspections/${cycleId}/edit`)}
        >
          <i className="bx bx-pencil" aria-hidden="true" /> Edit Cycle
        </button>
      )}
      <button
        type="button"
        className="inspection-preview-page__engagements"
        onClick={() =>
          navigate(`/inspections/${cycleId}/edit?step=engagements`)
        }
      >
        <i className="bx bx-group" aria-hidden="true" /> Manage Engagements
      </button>
      {canManage && (
        <div className="inspection-preview-page__more" ref={moreMenuRef}>
          <button
            type="button"
            className="inspection-preview-page__more-button"
            aria-label="More cycle actions"
            aria-expanded={moreMenuOpen}
            aria-haspopup="menu"
            onClick={() => setMoreMenuOpen((open) => !open)}
          >
            <i className="bx bx-dots-horizontal-rounded" aria-hidden="true" />
          </button>
          {moreMenuOpen && (
            <div className="inspection-preview-page__more-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMoreMenuOpen(false);
                  setArchiveConfirmationOpen(true);
                }}
              >
                <i className="bx bx-archive-in" aria-hidden="true" /> Archive
                cycle
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <FullScreenPageLayout
      className="inspection-cycle-summary-page"
      breadcrumbs={[
        { label: returnLabel, to: returnPath },
        { label: "Inspection Cycle Preview" },
      ]}
      showAskPiers={false}
      showUserInfo
      actions={
        <button type="button" onClick={() => navigate(returnPath)}>
          <i className="bx bx-x" aria-hidden="true" /> Close
        </button>
      }
    >
    <div className="inspection-preview-page">
      <button
        type="button"
        onClick={() =>
          navigate(returnPath, { state: fromEngagement ? null : { inspectionSlide: "back" } })
        }
        className="inspection-preview-page__back"
      >
        <i className="bx bx-left-arrow-alt" aria-hidden="true" /> {returnLabel}
      </button>

      <InspectionCycleOverview
        inspectionCycleId={Number(cycleId)}
        actions={renderActions}
        canCreateInspection={canManage}
      />

      <Modal
        centered
        isOpen={archiveConfirmationOpen}
        toggle={() => setArchiveConfirmationOpen(false)}
      >
        <ModalHeader toggle={() => setArchiveConfirmationOpen(false)}>
          Archive inspection cycle?
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            Archiving removes this inspection cycle from the active list. Its
            details, linked engagements, and activity history are retained.
          </p>
          <p className="mb-0 text-muted">
            Archive functionality is currently pending implementation and will
            be available in a future release.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="light"
            onClick={() => setArchiveConfirmationOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="danger"
            disabled
            title="Archive functionality is pending implementation"
          >
            Archive
          </Button>
        </ModalFooter>
      </Modal>
    </div>
    </FullScreenPageLayout>
  );
}
